import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
    DoorNoneCode,
    KompasFrameCode,
    MountPerimeterCode,
    NoMountCode,
} from "@/app/constants";

import { useSpacesStore } from "./spaces.store";
import { MountHardware } from "@/app/domain/entities/hardware";
import { MountsService } from "@/app/services/mounts.service";
import { useNewEstimateStore } from "./estimateStore";
import { useDoorsStore } from "./doors.store";
import { Mounts } from "@/app/domain/entities/mounts";
import {
    isKompasSystem,
    isSteelSystem,
} from "@/app/predicates/systems.predicates";
import { usePartitionStore } from "./partition.store";
import { Partition } from "@/app/domain";

export const useMountsStore = defineStore(
    "mounts-store",
    () => {
        const spacesStore = useSpacesStore();
        const partitionsStore = usePartitionStore();
        const estimateStore = useNewEstimateStore();
        const doorsStore = useDoorsStore();

        const activePartition = computed(
            () => partitionsStore.activePartitions,
        );
        const activeSpace = computed(() => spacesStore.activeSpace);
        const activeDoor = computed(() => doorsStore.activeDoor);

        const installedSystem = computed(
            () => estimateStore.estimate.installedSystemCode,
        );

        const activeMount = ref<Mounts | null>(null);
        const activeMountsHardwares = ref<MountHardware[]>([]);

        const hasActiveMounts = computed(() => Boolean(activeMount.value));
        const dataFromEstimate = ref(false);

        const $reset = () => {
            const partMount = activePartition.value?.getActiveMount()
            if (partMount?.id) {
                activePartition.value?.removeComponent(partMount.id, 'mount');
            }
            activeMount.value = null;
            activeMountsHardwares.value = [];
            dataFromEstimate.value = false;
        };

        const $restoreMounts = (spaces) => {
            setActiveMounts(activePartition.value);

            const isMountExist = spaces.filter((space) => space.getActiveMountHardwares()?.length > 0)
            dataFromEstimate.value = isMountExist.length > 0;
        };

        const setActiveMounts = (partition?: Partition) => {
            if (!partition) return;

            const mount = partition?.getActiveMount();

            activeMount.value = mount;
            activeMountsHardwares.value = mount?.getHardwares() || [];
        };

        const addMount = () => {
            if (!activePartition.value) {
                return;
            }

            const mount = new Mounts({
                structureType: getDefaultMountStructureType(),
            });

            activePartition.value.addComponent(mount);
            activeMount.value = mount;
        };

        const addMountHardware = () => {
            if (!activePartition.value || !activeDoor.value) {
                return null;
            }

            const hardwares = MountsService.createMountsHardwares({
                installedSystemCode: installedSystem.value,
                activeDoorStructureType:
                    activeDoor.value?.structureType || DoorNoneCode,
                activeMountStructureType:
                    activeMount.value?.structureType || getDefaultMountStructureType(),
                partition: activePartition.value,
            });

            activeMount.value?.replaceMountHardwares(hardwares);
            activeMountsHardwares.value = hardwares;
        };

        const getDefaultMountStructureType = () => {
            if (isSteelSystem(installedSystem.value)) {
                return NoMountCode;
            }

            if (isKompasSystem(installedSystem.value)) {
                return KompasFrameCode;
            }

            return MountPerimeterCode;
        };

        return {
            addMountHardware,
            addMount,
            setActiveMounts,
            $reset,
            $restoreMounts,

            activeMount,
            activeMountsHardwares,
            hasActiveMounts,
            dataFromEstimate
        };
    },
    { persist: true },
);
